import { Action,configureStore, ThunkAction } from '@reduxjs/toolkit'
import {
  type Action as ReduxAction,
  combineReducers,
  type Reducer
} from 'redux'
import {
  createTransform,
  FLUSH,
  PAUSE,
  PERSIST,
  type PersistConfig,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE} from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { emptyEditedProduct } from 'constants/productsStateDefaults'
import aiAnalyticsSlice from 'services/aiAnalyticsSlice'
import aiLimitsSlice from 'services/aiLimitsSlice'
import assetsReducer from 'services/assetsSlice'
import authReducer from 'services/authSlice'
import landingPagesSlice, {
  type ILandingPagesState
} from 'services/landingPagesSlice'
import packagesSlice from 'services/packagesSlice'
import partnersSlice from 'services/partnersSlice'
import productsSlice from 'services/productsSlice'
import rolesSlice from 'services/rolesSlice'
import translationsSlice from 'services/translationsSlice'
import userManagementSlice from 'services/userManagementSlice'
import { IProductsState } from 'types/slices'
import { LoadingStatus } from 'types/user'

const rootReducer = combineReducers({
  auth: authReducer,
  assets: assetsReducer,
  products: productsSlice,
  partners: partnersSlice,
  packages: packagesSlice,
  translations: translationsSlice,
  roles: rolesSlice,
  aiLimits: aiLimitsSlice,
  userManagement: userManagementSlice,
  aiAnalytics: aiAnalyticsSlice,
  landingPages: landingPagesSlice
})

/** Combined slice state before persistence (no `_persist`). */
export type RootReducerState = ReturnType<typeof rootReducer>

/**
 * Do not persist server-backed product snapshots: `cachedProducts`, and the
 * in-memory `editedProduct` used for product edit / preview (stale URLs and
 * nested assets after others edit). Pages refetch via `getProduct` on mount.
 */
const stripVolatileProductsTransform = createTransform<
  IProductsState,
  IProductsState,
  RootReducerState
>(
  (state: IProductsState) => ({
    ...state,
    cachedProducts: {},
    editedProduct: emptyEditedProduct,
    listStatus:
      state.products?.length > 0 ? LoadingStatus.loaded : LoadingStatus.initial
  }),
  (state: IProductsState) => ({
    ...state,
    cachedProducts: {},
    editedProduct: emptyEditedProduct,
    listStatus:
      state.products?.length > 0 ? LoadingStatus.loaded : LoadingStatus.initial
  }),
  { whitelist: ['products'] }
)

/**
 * LP slice is persisted for convenience, but server-backed compose data and
 * in-flight flags must not survive reload (stuck loading / stale variants cache).
 */
const resetLandingPagesVolatilePersistTransform = createTransform<
  ILandingPagesState,
  ILandingPagesState,
  RootReducerState
>(
  subState => ({
    ...subState,
    landingPageAssetsListPendingCount: 0,
    landingPagesListStatus: LoadingStatus.initial,
    variantsListLoading: false,
    configLoading: false,
    brandFontsLoading: false,
    variantsList: null,
    variantsListBrandSlug: null,
    brandFonts: null,
    brandFontsBrandId: null
  }),
  subState => ({
    ...subState,
    landingPageAssetsListPendingCount: 0,
    landingPagesListStatus: LoadingStatus.initial,
    variantsListLoading: false,
    configLoading: false,
    brandFontsLoading: false,
    variantsList: null,
    variantsListBrandSlug: null,
    brandFonts: null,
    brandFontsBrandId: null
  }),
  { whitelist: ['landingPages'] }
)

const persistConfig = {
  key: 'root',
  blacklist: ['auth'],
  storage,
  transforms: [
    stripVolatileProductsTransform,
    resetLandingPagesVolatilePersistTransform
  ]
} satisfies PersistConfig<RootReducerState>

const persistedReducer = persistReducer<RootReducerState>(
  persistConfig,
  rootReducer
)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    })
})

export const persistor = persistStore(store)

export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof store.getState>
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>
